import * as Sentry from '@sentry/react'
import { Button, Loader } from '../../../components'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import { getLabel } from '../../../services'
import { useQuery, useQueryClient } from 'react-query'
import { Trans, useTranslation } from 'react-i18next'
// Initialises i18next for every screen and test that renders the dialog outside the app shell.
import '../../../translations'

export const LabelView = ({ title, type, copies = 1, is_replenishment_label = false, file_path = null, bundles = null, onCancel, onPrint }) => {
  const [path, setPath] = useState(null)
  const [bundleUrls, setBundleUrls] = useState([])
  const [printError, setPrintError] = useState(null)
  const { t } = useTranslation()
  const lv = (key, options) => t(`generate_shipment.label_view.${key}`, options)
  const pdf2base64 = require('pdf-to-base64')
  const b64toBlob = require('b64-to-blob')
  useEffect(() => {
    if (file_path) {
      // If the backend hands us an already-absolute URL (S3, CDN,
      // full https://... link) we must NOT prepend PLATFORM_URL —
      // that produced malformed URLs like
      // "https://api.cubbo.comhttps://s3.../label.pdf" which either
      // 404'd or redirected to WMS routes, causing the WMS UI to
      // render inside the label preview embed.
      const isAbsolute = /^https?:\/\//i.test(file_path)
      if (is_replenishment_label || isAbsolute) {
        setPath(file_path)
      } else {
        let url = ''
        url = process.env.REACT_APP_PLATFORM_URL + file_path
        setPath(url)
      }
    }
  }, [title, file_path])

  // Bundle mode: the backend already sent each paper stock as base64, so the blob is built
  // locally instead of re-fetching a URL that has to survive the ActiveStorage redirect to S3.
  useEffect(() => {
    if (!bundles || !bundles.length) {
      setBundleUrls([])
      return
    }

    const urls = []
    bundles.forEach(bundle => {
      try {
        const blob = b64toBlob(bundle.file_base64, bundle.content_type || 'application/pdf')
        urls.push({ paper_class: bundle.paper_class, url: URL.createObjectURL(blob) })
      } catch (error) {
        // Counted as a bundle that never produced paper, which keeps the operator on the order.
        Sentry.captureException(error)
      }
    })
    setBundleUrls(urls)

    return () => urls.forEach(entry => URL.revokeObjectURL(entry.url))
  }, [bundles])

  const handleClickOnPrintBundles = () => {
    setPrintError(null)

    bundleUrls.forEach(entry => {
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      // Print on load, not right after appendChild: a blob src is not parsed synchronously, so
      // an immediate print() can reach an iframe whose document is still empty.
      iframe.onload = () => iframe.contentWindow?.print()
      iframe.src = entry.url
      document.body.appendChild(iframe)
    })

    const failed = bundles.length - bundleUrls.length
    if (failed > 0) {
      setPrintError(lv('bundle_prepare_failed', { failed, total: bundles.length }))
    }

    setTimeout(() => {
      onPrint({ printed: bundleUrls.length, failed })
    }, 500)
  }

  const handleClickOnPrint = () => {
    setPrintError(null)
    pdf2base64(path)
      .then(response => {
        for (let i = 0; i < copies; i++) {
          const contentType = 'application/pdf'
          const b64Data = response
          const blob = b64toBlob(b64Data, contentType)
          const blobUrl = URL.createObjectURL(blob)
          const iframe = document.createElement('iframe')
          iframe.style.display = 'none'
          iframe.src = blobUrl
          document.body.appendChild(iframe)
          // Imprimir la página
          iframe.contentWindow.print()
        }
        setTimeout(() => {
          onPrint()
        }, 500)
      })
      .catch(error => {
        // The pdf2base64 library does an XHR/fetch against `path`.
        // For URLs that redirect to S3/CDN without permissive CORS
        // this rejects. Historically the .catch was silent; we
        // surface a banner + auto-open the PDF in a new tab so the
        // operator can print via the browser's native viewer
        // (window.open is a top-level navigation, not a fetch —
        // no CORS restriction, cookies attached, 302s followed).
        //
        // Same behavior operators used to get from the embed PDF
        // viewer's "print" button before we introduced the
        // pdf2base64 path. Preserves the "always able to print"
        // flow that shipped before PR #477.
        console.log(error)
        setPrintError(error?.message || lv('print_failed'))
        if (path) {
          const newTab = window.open(path, '_blank', 'noopener,noreferrer')
          // Some browsers open the tab in the background — if
          // the tab reference isn't valid, we just leave the
          // banner + inline link so the operator can still click.
          if (newTab) {
            try {
              newTab.focus()
            } catch (_) {
              /* ignore */
            }
          }
        }
      })
  }

  const handleClickOnCancel = () => {
    if (onCancel) {
      onCancel()
    }
  }

  const inBundleMode = Boolean(bundles && bundles.length)
  const previewPath = inBundleMode ? bundleUrls[0] && bundleUrls[0].url : path

  return (
    <div style={{ width: '50vw' }} className="relative">
      <div className="space-y-3 divide-y divide-gray-200">
        <div className="grid grid-cols-1 gap-y-2 gap-x-4 sm:grid-cols-2">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">{title}</h3>
          </div>
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {inBundleMode ? (
                <Trans i18nKey="generate_shipment.label_view.bundle_count" values={{ n: bundleUrls.length }} components={{ bold: <b /> }} />
              ) : (
                <Trans
                  i18nKey="generate_shipment.label_view.print_type"
                  values={{ type: lv(type == 'label' ? 'type_label' : 'type_document') }}
                  components={{ bold: <b /> }}
                />
              )}
            </h3>
          </div>
        </div>

        <div className="pt-4">
          {!previewPath ? (
            <>
              <Loader show={true}></Loader>
            </>
          ) : (
            <div>
              {/* <object type="application/pdf"> refuses to
                                render non-PDF content and shows the fallback
                                <a> instead. Prevents the "WMS UI rendered
                                inside the print dialog" bug when the label
                                URL 302-redirects to a WMS route (auth /
                                cross-origin edge case with the Rails
                                ActiveStorage blob redirect endpoint).

                                key={previewPath} forces React to mount a fresh
                                <object> whenever the previewed file changes.
                                The browser does not reload the PDF on a
                                data-attribute change, so during the packing
                                auto-print cascade (guía → attached documents)
                                every file after the first rendered blank when
                                the element was reused. */}
              <object id="label" key={previewPath} type="application/pdf" data={previewPath} height="500" width="100%">
                <div className="p-6 text-center text-sm text-gray-600 bg-gray-50 rounded">
                  <p className="mb-2">{lv('preview_failed')}</p>
                  <a href={previewPath} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 underline">
                    {lv('open_in_new_tab')}
                  </a>
                </div>
              </object>
            </div>
          )}
          {printError && inBundleMode && (
            <div className="mt-4 p-3 rounded bg-yellow-50 border border-yellow-200 text-sm text-yellow-800">
              <p>{printError}</p>
            </div>
          )}
          {printError && !inBundleMode && (
            <div className="mt-4 p-3 rounded bg-yellow-50 border border-yellow-200 text-sm text-yellow-800">
              <p className="mb-2">{lv('opened_in_new_tab')}</p>
              {previewPath && (
                <a href={previewPath} target="_blank" rel="noopener noreferrer" className="text-yellow-900 hover:text-yellow-950 underline font-medium">
                  {lv('open_in_new_tab')}
                </a>
              )}
            </div>
          )}
          <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
            <Button type="secondary" onClick={handleClickOnCancel}>
              {lv('cancel')}
            </Button>
            <Button onClick={inBundleMode ? handleClickOnPrintBundles : handleClickOnPrint}>{lv('print')}</Button>
          </div>
        </div>
      </div>
    </div>
  )
}
